// SelectionBus — how the two panes of the split search talk.
//
// The whole point of the design (docs/2d-3d-search.md) is that there is
// no link table, so this bus does NOT resolve a selection to its
// counterpart. It carries the selection, and whoever is interested
// derives a query from it and runs the ordinary search. Typing in the
// search box and clicking an element enter the same pipe.
//
// It is a module-level emitter rather than React context because one of
// its subscribers is inside the legacy 3D viewer page, which is mounted
// as a black box and has no way to reach a provider above it.
const listeners = new Set();

let current = null; // { world, anchor, query, projectId }

/**
 * Announce a selection.
 * @param {{world:'2d'|'3d', anchor:object, query:string, projectId:string}} sel
 */
export function select(sel) {
  current = sel || null;
  for (const fn of [...listeners]) {
    // One bad subscriber must not stop the others - a pane that has
    // unmounted mid-notify is the normal case, not an exception.
    try { fn(current); } catch (e) { /* ignore */ }
  }
}

/** Subscribe. Returns the unsubscribe function. */
export function subscribe(fn) {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The last selection, for a pane that mounts after one was made. */
export function currentSelection() {
  return current;
}

/** Forget everything - used when the project changes. */
export function reset() {
  current = null;
}

/**
 * The query that finds this anchor in the OTHER world.
 *
 * Tags first, because an exact token is the strongest thing either world
 * can agree on; the type text is the fallback for an element nobody
 * tagged. This is the only "linking" the design has, and it is a string.
 */
export function queryFor(anchor) {
  if (!anchor) return "";
  if (Array.isArray(anchor.tags) && anchor.tags.length) return anchor.tags.join(" ");
  return String(anchor.text || "").slice(0, 120);
}

export default { select, subscribe, currentSelection, reset, queryFor };
